/**
 * Response shapes, copied field-for-field from the real API types — never
 * approximated. Sources, so a drift is easy to catch on the next read:
 *
 *   Category            apps/api/src/modules/catalog/category-repository.ts
 *   Product              apps/api/src/modules/catalog/product-repository.ts
 *   ProductWithStock     apps/api/src/modules/catalog/http/product.controller.ts
 *   ProductVariant        apps/api/src/modules/catalog/product-variant-repository.ts
 *   ProductVariantWithStock apps/api/src/modules/catalog/http/product-variant.controller.ts
 *   ProductImage          apps/api/src/modules/catalog/product-image-repository.ts
 *   DerivedStock/StockState apps/api/src/modules/inventory/inventory-state-resolver.ts
 *   Inventory              apps/api/src/modules/inventory/inventory-repository.ts
 */

export type ProductStatus = 'draft' | 'active' | 'inactive';

export interface Category {
  readonly id: string;
  readonly tenantId: string;
  readonly parentCategoryId: string | null;
  readonly name: string;
  readonly slug: string;
  readonly imageUrl: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
  readonly isActive: boolean;
  readonly seoTitle: string | null;
  readonly seoDescription: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface Product {
  readonly id: string;
  readonly tenantId: string;
  readonly categoryId: string | null;
  readonly name: string;
  readonly slug: string;
  readonly shortDescription: string | null;
  readonly longDescription: string | null;
  readonly sku: string;
  readonly barcode: string | null;
  readonly price: number;
  readonly compareAtPrice: number | null;
  readonly purchasePrice: number | null;
  readonly vatRate: number | null;
  readonly weight: number | null;
  readonly dimensions: Record<string, unknown> | null;
  readonly brand: string | null;
  readonly status: ProductStatus;
  readonly seoTitle: string | null;
  readonly seoDescription: string | null;
  readonly metadata: Record<string, unknown>;
  /** Variant-improvements lot — NULL until a merchant dismisses the "Vérifiez vos options" review banner; never gates visibility or sale. */
  readonly variantOptionsReviewedAt: string | null;
  /** Lot 1 / 1C - when the merchant marked this product a "coup de coeur" (migration 0065); NULL = not featured. */
  readonly featuredAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Lot 1 / 1C - the most products a shop can feature at once. Mirrors
 * `MAX_FEATURED_PRODUCTS` in apps/api/src/modules/catalog/product-repository.ts,
 * which is the enforcement: this copy only words the message and disables
 * nothing on its own.
 */
export const MAX_FEATURED_PRODUCTS = 5;

export type StockState = 'in_stock' | 'low_stock' | 'out_of_stock' | 'temporarily_unavailable';

export interface DerivedStock {
  readonly state: StockState;
  readonly quantityAvailable: number | null;
  readonly quantityMinThreshold: number | null;
}

export interface ProductWithStock extends Product {
  readonly stock: DerivedStock;
}

export interface ProductVariant {
  readonly id: string;
  readonly tenantId: string;
  readonly productId: string;
  readonly sku: string;
  readonly attributes: Record<string, unknown>;
  readonly priceOverride: number | null;
  /** Variant-improvements lot — false = temporarily stopped selling, never deleted. */
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Variant-improvements lot — structured options (`product-option-repository.ts`).
 * Source of truth for the merchant's option builder; `ProductVariant.attributes`
 * stays the stable read-side projection everything else already reads.
 */
export interface ProductOptionValue {
  readonly id: string;
  readonly optionId: string;
  readonly value: string;
  readonly sortOrder: number;
  readonly swatchHex: string | null;
}

export type ProductOptionDisplayType = 'button' | 'color_swatch';

export interface ProductOption {
  readonly id: string;
  readonly tenantId: string;
  readonly productId: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly displayType: ProductOptionDisplayType;
  readonly autoGenerated: boolean;
}

export interface ProductOptionWithValues extends ProductOption {
  readonly values: readonly ProductOptionValue[];
}

export interface ProductVariantWithStock extends ProductVariant {
  readonly stock: DerivedStock;
}

export interface ProductImage {
  readonly id: string;
  readonly tenantId: string;
  readonly productId: string;
  readonly url: string;
  readonly sortOrder: number;
  readonly altText: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** `PUT /merchant/inventory/:variantId/adjust`'s response — the ONLY shape that ever carries `quantityReserved` (see table-utils.ts / InventoryPage's own caveat). */
export interface Inventory {
  readonly id: string;
  readonly tenantId: string;
  readonly productId: string | null;
  readonly productVariantId: string | null;
  readonly warehouseId: string;
  readonly quantityAvailable: number;
  readonly quantityReserved: number;
  readonly quantityMinThreshold: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}
